import {
  MAX_SHIFT_PATTERNS,
  supervisorsFromYupContext,
  supervisorIdentityKey,
} from "./bookingFormShared";

export function buildBookingValidationSchema(
  Yup,
  { validateUrgentBooking, diffHours, hhmmToMinutes, minBreakMinutesFor, dayjs },
  {
    hasExistingPoDocument = false,
    includeShiftFields = true,
    includeAgreements = true,
    includeLocationAndTimezone = true,
  } = {}
) {
  const PatternSchema = Yup.object({
    sectorSlug: Yup.string().required("Sector is required"),
    roleSlug: Yup.string().required("Role is required"),
    quantity: Yup.number().integer().min(1).required("Quantity required"),
    hasPPEProvided: Yup.mixed().oneOf(["yes", "not_required"]).required(),
    ppeInstruction: Yup.string().when("hasPPEProvided", {
      is: "yes",
      then: (s) =>
        s.trim().required("PPE instruction required when PPE is provided"),
      otherwise: (s) => s.notRequired(),
    }),
    startDate: Yup.string()
      .required("Start date is required")
      .test(
        "booking-validation",
        "Invalid start date for booking",
        function (v) {
          const { startTime, endTime } = this.parent;
          const isUrgent = this.from[this.from.length - 1].value.isUrgent;
          if (!v || !startTime) return true;

          const error = validateUrgentBooking(v, startTime, isUrgent, endTime);
          if (error) {
            return this.createError({ message: error });
          }
          return true;
        }
      ),
    endDate: Yup.string()
      .required("End date is required")
      .test("after-start", "End date must be after start date", function (v) {
        const { startDate } = this.parent;
        return (
          v &&
          startDate &&
          (dayjs(v).isSame(dayjs(startDate), "day") ||
            dayjs(v).isAfter(dayjs(startDate), "day"))
        );
      }),
    excludeWeekend: Yup.boolean(),
    excludeSpecificDates: Yup.string(),
    dates: Yup.array()
      .of(Yup.string().required())
      .min(1, "Add at least one date"),
    startTime: Yup.string()
      .required("Start time required")
      .test(
        "booking-time-validation",
        "Invalid start time for booking",
        function (v) {
          const { startDate, endTime } = this.parent;
          const isUrgent = this.from[this.from.length - 1].value.isUrgent;
          if (!v || !startDate) return true;

          const error = validateUrgentBooking(startDate, v, isUrgent, endTime);
          if (error) {
            return this.createError({ message: error });
          }
          return true;
        }
      ),
    endTime: Yup.string()
      .required("End time required")
      .test(
        "after-start",
        "End must be after Start (overnight allowed)",
        function (v) {
          const { startTime } = this.parent;
          return v && startTime && v !== startTime;
        }
      )
      .test(
        "urgent-24hour-window",
        "Invalid end time for urgent booking",
        function (v) {
          const { startDate, startTime } = this.parent;
          const isUrgent = this.from[this.from.length - 1].value.isUrgent;
          if (!v || !startDate || !startTime || !isUrgent) return true;

          const error = validateUrgentBooking(
            startDate,
            startTime,
            isUrgent,
            v
          );
          if (error) {
            return this.createError({ message: error });
          }
          return true;
        }
      ),
    breakTime: Yup.string()
      .required("Break time required")
      .test("min-break", "Break time below required minimum", function (v) {
        const { startTime, endTime } = this.parent;
        if (!startTime || !endTime || !v) return true;
        const hours = diffHours(startTime, endTime);
        const minMins = minBreakMinutesFor(hours);
        return hhmmToMinutes(v) >= minMins;
      }),
    supervisor: Yup.array()
      .of(
        Yup.object({
          source: Yup.mixed().oneOf(["existing", "new"]).required(),
          supervisorSlug: Yup.string().when("source", {
            is: "existing",
            then: (s) =>
              s
                .trim()
                .required("Select a supervisor")
                .test(
                  "unique-in-shift",
                  "This supervisor is already added to this shift",
                  function (value) {
                    if (!value) return true;
                    const supervisors = supervisorsFromYupContext(this.from);
                    const count = supervisors.filter(
                      (row) =>
                        row?.source === "existing" &&
                        row?.supervisorSlug === value
                    ).length;
                    return count <= 1;
                  }
                ),
            otherwise: (s) => s.notRequired(),
          }),
          name: Yup.string().when("source", {
            is: "new",
            then: (s) =>
              s
                .trim()
                .required("Name required")
                .min(2, "Name must be at least 2 characters"),
            otherwise: (s) => s.notRequired(),
          }),
          mobile: Yup.string().when("source", {
            is: "new",
            then: (s) =>
              s
                .required("Mobile required")
                .test(
                  "phone-valid",
                  "Please enter a valid phone number",
                  function (value) {
                    if (!value) return false;
                    const digitsOnly = value.replace(/\D/g, "");
                    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
                  }
                ),
            otherwise: (s) => s.notRequired(),
          }),
          email: Yup.string().when("source", {
            is: "new",
            then: (s) =>
              s
                .email("Please enter a valid email address")
                .required("Email is required")
                .test(
                  "unique-in-shift",
                  "A supervisor with this email is already added to this shift",
                  function (value) {
                    if (!value) return true;
                    const email = value.trim().toLowerCase();
                    const supervisors = supervisorsFromYupContext(this.from);
                    const count = supervisors.filter(
                      (row) =>
                        row?.source === "new" &&
                        String(row?.email || "")
                          .trim()
                          .toLowerCase() === email
                    ).length;
                    return count <= 1;
                  }
                ),
            otherwise: (s) => s.notRequired(),
          }),
        })
      )
      .min(1, "Add at least one supervisor")
      .test(
        "unique-supervisors",
        "Each supervisor can only be added once per shift",
        function (supervisors) {
          if (!supervisors?.length) return true;
          const seen = new Map();
          for (let i = 0; i < supervisors.length; i++) {
            const key = supervisorIdentityKey(supervisors[i]);
            if (!key) continue;
            if (seen.has(key)) {
              const isExisting = supervisors[i]?.source === "existing";
              return this.createError({
                path: `${i}.${isExisting ? "supervisorSlug" : "email"}`,
                message: isExisting
                  ? "This supervisor is already added to this shift"
                  : "A supervisor with these details is already added to this shift",
              });
            }
            seen.set(key, i);
          }
          return true;
        }
      ),
    shiftDetails: Yup.string().required("Shift details required"),
  });

  const shiftPatternField = includeShiftFields
    ? {
        isUrgent: Yup.boolean().required(),
        shiftPattern: Yup.array()
          .of(PatternSchema)
          .min(1)
          .when("isUrgent", {
            is: true,
            then: (schema) =>
              schema.max(1, "Urgent bookings can only have one shift pattern"),
            otherwise: (schema) =>
              schema.max(
                MAX_SHIFT_PATTERNS,
                "You can add up to three shift patterns"
              ),
          }),
      }
    : {};

  return Yup.object({
    ...shiftPatternField,
    hasPORequired: Yup.boolean().required(),
    poReference: Yup.string().when("hasPORequired", {
      is: true,
      then: (s) => s.trim().required("PO reference required"),
      otherwise: (s) => s.notRequired(),
    }),
    poDocument: Yup.mixed().when("hasPORequired", {
      is: true,
      then: (s) =>
        hasExistingPoDocument
          ? s.notRequired()
          : s.required("Official PO document required"),
      otherwise: (s) => s.notRequired(),
    }),
    ...(includeLocationAndTimezone
      ? {
          timezone: Yup.string().required("Timezone required"),
          location: Yup.object({
            siteName: Yup.string().required("Site name required"),
            searchAddress: Yup.string().notRequired(),
            fullAddress: Yup.string().required("Full address required"),
            accessNotes: Yup.string().notRequired(),
            latitude: Yup.number().required("Latitude required"),
            longitude: Yup.number().required("Longitude required"),
          }),
        }
      : {}),
    notes: Yup.string().notRequired(),
    ...(includeAgreements
      ? {
          agreements: Yup.object({
            confirmAccurate: Yup.boolean().oneOf([true], "Required"),
            qrCheckin: Yup.boolean().oneOf([true], "Required"),
            hnsResponsibility: Yup.boolean().oneOf([true], "Required"),
            terms: Yup.boolean().oneOf([true], "Required"),
          }),
        }
      : {}),
  });
}
